import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import SignportGate from './pages/SignportGate';
import FundingConfirmation from './pages/FundingConfirmation';
import FundingConfirmationInbox from './pages/FundingConfirmationInbox';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { TenantProvider } from './contexts/TenantContext';
import Landing from './pages/Landing';
import Login from './pages/Login';
import GoogleCallback from './pages/GoogleCallback';
import MilaMemory from './pages/MilaMemory';
import Dashboard from './pages/Dashboard';
import Contacts from './pages/Contacts';
import ContactMerge from './pages/ContactMerge';
import ContactProfile from './pages/ContactProfile';
import Contracts from './pages/Contracts';
import ContractTemplateMapping from './pages/ContractTemplateMapping';
import Deals from './pages/Deals';
import DealDetail from './pages/DealDetail';
import SyndicatedDeals from './pages/SyndicatedDeals';
import SyndicationWithdrawals from './pages/SyndicationWithdrawals';
import Portfolio from './pages/Portfolio';
import BulkImport from './pages/BulkImport';
import SheetImport from './pages/SheetImport';
import MCATransactionImport from './pages/MCATransactionImport';
import { isRetired, RETIRED_REDIRECT_TO } from './config/retiredPages';
import AdminJobs from './pages/AdminJobs';
import Users from './pages/Users';
import Commissions from './pages/Commissions';
import DealApplications from './pages/DealApplications';
import ApplicationReview from './pages/ApplicationReview';
import Reports from './pages/Reports';
import Notifications from './pages/Notifications';
import Settings from './pages/Settings';
import DataHealthDashboard from './pages/DataHealthDashboard';
import RefiChainExplorer from './pages/RefiChainExplorer';
import CommissionsImport from './pages/CommissionsImport';
import UploadsPage from './pages/UploadsPage';
import Legal from './pages/Legal';
import Collections from './pages/Collections';
import Inbox from './pages/Inbox';
import DailyImportReview from './pages/DailyImportReview';
// Mockup route + component removed Feb 2026 — production uses SummaryTabV2.
import NotFound from './pages/NotFound';
import SyndicationDashboard from './pages/SyndicationDashboard';
import UWToolkit from './pages/UWToolkit';
import UWAdmin from './pages/UWAdmin';
import SyndicationCapitalDashboard from './pages/syndication/SyndicationCapitalDashboard';
import SyndicationLedger from './pages/syndication/SyndicationLedger';
import SyndicationPayables from './pages/syndication/SyndicationPayables';
import DisbursementsHub from './pages/syndication/DisbursementsHub';  // 11/03 Josh: Withdrawals + Requests + cart in one page
import MoneyOut from './pages/syndication/MoneyOut';  // 11/03 Josh: Payouts + ISO Commissions + Partner Ledger in one page
import SyndicatorFeeLedger from './pages/syndication/SyndicatorFeeLedger';
import SyndicatorPortal from './pages/syndication/SyndicatorPortal';
import SyndicatorDetail from './pages/SyndicatorDetail';
import SyndicationOffering from './pages/syndication/SyndicationOffering';
import SyndicationResponse from './pages/syndication/SyndicationResponse';
import SyndicationDealReport from './pages/syndication/SyndicationDealReport';
import UnsyndicatedDeals from './pages/syndication/UnsyndicatedDeals';
import { isLegacyAdmin, isSuperAdmin, pageAllowed } from './lib/features';
import './App.css';

// 11/03: admins land on Money out → ISO commissions; brokers / ISOs keep the commission-request page.
const CommissionsEntry = () => { const { user } = useAuth(); return user?.roles?.includes('admin') ? <Navigate to="/syndications/money-out?tab=isos" replace /> : <Commissions />; };

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  // While we are checking the stored token with /auth/me, avoid redirecting
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!user) {
    // 10/09: remember where the visitor was going (e.g. /signport) so Login can send them on after signing in.
    const next = location?.pathname && location.pathname !== '/' ? `?next=${encodeURIComponent(location.pathname + (location.search || ''))}` : '';
    return <Navigate to={`/login${next}`} replace />;
  }
  return children;
};

const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!user) return <Navigate to="/dashboard" replace />;
  // 11/02 (Josh): feature-based access — a feature-mapped page opens on the effective feature set; a legacy admin
  // (role, no explicit set) and a super admin keep every page; unmapped pages keep the admin-role rule.
  if (!(isSuperAdmin(user) || isLegacyAdmin(user))) {
    const verdict = pageAllowed(user, location.pathname);
    if (verdict === false || (verdict === null && !user.roles?.includes('admin'))) {
      return <Navigate to="/dashboard" replace />;
    }
  }
  return children;
};

// 10/07/2026: super admin only (user management). A super admin also carries 'admin'.
const SuperAdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }
  if (!user || !user.roles?.includes('super_admin')) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

// Route for `/` — shows Landing to guests, redirects authenticated users to /dashboard
const LandingRoute = () => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }
  if (user) return <Navigate to="/dashboard" replace />;
  return <Landing />;
};

const AppRouter = () => {
  return (
    <Routes>
      <Route path="/" element={<LandingRoute />} />
      <Route path="/confirm-funding" element={<FundingConfirmation />} />
      <Route path="/funding-confirmations" element={<AdminRoute><FundingConfirmationInbox /></AdminRoute>} />  {/* 10/23 Josh: inbox tile */}
      <Route path="/login" element={<Login />} />
      <Route path="/auth/google/callback" element={<GoogleCallback />} />
      <Route path="/mila-memory" element={<AdminRoute><MilaMemory /></AdminRoute>} />
      {/* Jobs Engine (09/22/2026) — scheduled jobs admin */}
      <Route path="/admin/jobs" element={<SuperAdminRoute><AdminJobs /></SuperAdminRoute>} />  {/* 10/08 roles pass 3 */}
      <Route path="/syndication-response/:token" element={<SyndicationResponse />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/daily-import-review"
        element={
          <AdminRoute>
            <DailyImportReview />
          </AdminRoute>
        }
      />
      <Route
        path="/contacts"
        element={
          <ProtectedRoute>
            <Contacts />
          </ProtectedRoute>
        }
      />
      <Route
        path="/contacts/merge"
        element={
          <AdminRoute>
            <ContactMerge />
          </AdminRoute>
        }
      />
      <Route
        path="/contacts/:id"
        element={
          <ProtectedRoute>
            <ContactProfile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/contracts"
        element={
          <AdminRoute>
            <Contracts />
          </AdminRoute>
        }
      />
      <Route
        path="/contract-mapping"
        element={
          <AdminRoute>
            <ContractTemplateMapping />
          </AdminRoute>
        }
      />
      <Route
        path="/deals"
        element={
          <ProtectedRoute>
            <Deals />
          </ProtectedRoute>
        }
      />
      <Route
        path="/deals/:dealId/offering"
        element={
          <ProtectedRoute>
            <SyndicationOffering />
          </ProtectedRoute>
        }
      />
      <Route
        path="/syndications/syndicator/:sid/deal/:dealId/report"
        element={
          <ProtectedRoute>
            <SyndicationDealReport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/syndications/unsyndicated"
        element={
          <ProtectedRoute>
            <UnsyndicatedDeals />
          </ProtectedRoute>
        }
      />
      <Route path="/collections" element={<AdminRoute><Collections /></AdminRoute>} />  {/* 10/12 Collections Center (Aircall SMS) */}
      <Route path="/inbox" element={<AdminRoute><Inbox /></AdminRoute>} />  {/* 10/12 SMS inbox */}
      <Route
        path="/deals/:id"
        element={
          <ProtectedRoute>
            <DealDetail />
          </ProtectedRoute>
        }
      />
      <Route path="/commissions" element={<ProtectedRoute><CommissionsEntry /></ProtectedRoute>} />
      <Route
        path="/syndicated-deals"
        element={
          <ProtectedRoute>
            <SyndicatedDeals />
          </ProtectedRoute>
        }
      />
      <Route
        path="/syndication-withdrawals"
        element={
          <AdminRoute>
            <SyndicationWithdrawals />
          </AdminRoute>
        }
      />
      <Route
        path="/syndication-dashboard"
        element={
          <ProtectedRoute>
            <SyndicationDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/syndications/syndicator/:id"
        element={
          <ProtectedRoute>
            <SyndicatorDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/syndications/dashboard"
        element={
          <ProtectedRoute>
            <SyndicationCapitalDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/syndications/ledger"
        element={
          <ProtectedRoute>
            <SyndicationLedger />
          </ProtectedRoute>
        }
      />
      <Route path="/syndications/disbursements" element={<AdminRoute><DisbursementsHub /></AdminRoute>} />
      <Route path="/syndications/money-out" element={<AdminRoute><MoneyOut /></AdminRoute>} />
      <Route path="/withdrawal-requests" element={<Navigate to="/syndications/disbursements?tab=requests" replace />} />
      <Route path="/syndications/payouts" element={<Navigate to="/syndications/money-out?tab=payouts" replace />} />
      <Route path="/partner-ledger" element={<Navigate to="/syndications/money-out?tab=partners" replace />} />
      <Route
        path="/syndications/payables"
        element={
          <AdminRoute>
            <SyndicationPayables />
          </AdminRoute>
        }
      />
      <Route
        path="/syndications/fee-ledger"
        element={
          <ProtectedRoute>
            <SyndicatorFeeLedger />
          </ProtectedRoute>
        }
      />
      <Route
        path="/portal"
        element={
          <SyndicatorPortal />
        }
      />
      <Route path="/portal/:syndicatorId" element={<SyndicatorPortal />} />
      {/* 10/07 (Josh): Data Center retired — old import URLs land on Daily Import Review */}
      <Route path="/csv-import" element={<Navigate to="/daily-import-review" replace />} />
      {/* 09/22/2026 — retired upload pages redirect to Daily Import (config/retiredPages.js) */}
      <Route
        path="/bulk-import"
        element={isRetired('bulk-import') ? <Navigate to={RETIRED_REDIRECT_TO} replace /> : (
          <AdminRoute>
            <BulkImport />
          </AdminRoute>
        )}
      />
      <Route
        path="/sheet-import"
        element={isRetired('sheet-import') ? <Navigate to={RETIRED_REDIRECT_TO} replace /> : (
          <AdminRoute>
            <SheetImport />
          </AdminRoute>
        )}
      />
      <Route path="/push-live" element={<Navigate to="/daily-import-review" replace />} />
      <Route path="/daily-upload" element={<Navigate to="/daily-import-review" replace />} />
      <Route path="/manual-deal-history-entry" element={<Navigate to="/daily-import-review" replace />} />
      <Route
        path="/mca-import"
        element={isRetired('mca-import') ? <Navigate to={RETIRED_REDIRECT_TO} replace /> : (
          <AdminRoute>
            <MCATransactionImport />
          </AdminRoute>
        )}
      />
      <Route
        path="/legal"
        element={
          <AdminRoute>
            <Legal />
          </AdminRoute>
        }
      />
      {/* 09/28: the remittance importer lives inside the Legal page now */}
      <Route path="/lawyer-remittance" element={<Navigate to="/legal?tab=remittance" replace />} />
      <Route
        path="/portfolio"
        element={
          <ProtectedRoute>
            <Portfolio />
          </ProtectedRoute>
        }
      />
      <Route
        path="/users"
        element={
          <SuperAdminRoute>
            <Users />
          </SuperAdminRoute>
        }
      />
      <Route
        path="/deal-applications"
        element={
          <ProtectedRoute>
            <DealApplications />
          </ProtectedRoute>
        }
      />
      <Route
        path="/submit-application"
        element={
          <ProtectedRoute>
            <DealApplications mode="submit" />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-applications"
        element={
          <ProtectedRoute>
            <DealApplications mode="list" />
          </ProtectedRoute>
        }
      />
      <Route
        path="/application-review"
        element={
          <ProtectedRoute>
            <ApplicationReview />
          </ProtectedRoute>
        }
      />
      <Route
        path="/applications"
        element={
          <ProtectedRoute>
            <ApplicationReview />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute>
            <Reports />
          </ProtectedRoute>
        }
      />
      <Route
        path="/uw-toolkit"
        element={
          <ProtectedRoute>
            <UWToolkit />
          </ProtectedRoute>
        }
      />
      <Route
        path="/uw-admin"
        element={
          <AdminRoute>
            <UWAdmin />
          </AdminRoute>
        }
      />
      <Route
        path="/signport"
        element={
          <ProtectedRoute>
            <SignportGate />
          </ProtectedRoute>
        }
      />
      <Route
        path="/notifications"
        element={
          <ProtectedRoute>
            <Notifications />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/data-health"
        element={
          <SuperAdminRoute>
            <DataHealthDashboard />
          </SuperAdminRoute>
        }
      />
      <Route
        path="/refi-chains"
        element={
          <AdminRoute>
            <RefiChainExplorer />
          </AdminRoute>
        }
      />
      <Route
        path="/commissions-import"
        element={<Navigate to="/daily-import-review" replace />}
      />
      <Route
        path="/uploads"
        element={isRetired('uploads') ? <Navigate to={RETIRED_REDIRECT_TO} replace /> : (
          <AdminRoute>
            <UploadsPage />
          </AdminRoute>
        )}
      />
      <Route
        path="/uploads/history"
        element={isRetired('uploads') ? <Navigate to={RETIRED_REDIRECT_TO} replace /> : (
          <AdminRoute>
            <UploadsPage />
          </AdminRoute>
        )}
      />
      {/* Catch-all 404 — must be last */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

function App() {
  return (
    <AuthProvider>
      <TenantProvider>
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
      </TenantProvider>
    </AuthProvider>
  );
}

export default App;
